import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const LiveKitchen = () => {
  const [activeCamera, setActiveCamera] = useState(0);
  const [isLive, setIsLive] = useState(true);

  const kitchenCameras = [
    {
      id: 1,
      name: "Grill Station",
      description: "Watch our master grillers prepare fresh kebabs over open flames",
      image: "https://images.pexels.com/photos/5474640/pexels-photo-5474640.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      activity: "Grilling lamb kebabs",
      chef: "Chef Omar",
      temperature: "450°F",
      isActive: true
    },
    {
      id: 2,
      name: "Prep Kitchen",
      description: "Fresh ingredients being prepared with traditional techniques",
      image: "https://images.pexels.com/photos/4253302/pexels-photo-4253302.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      activity: "Making fresh hummus",
      chef: "Chef Layla",
      temperature: "Room temp",
      isActive: true
    },
    {
      id: 3,
      name: "Bread Oven",
      description: "Traditional pita bread baked fresh throughout the day",
      image: "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      activity: "Baking fresh pita",
      chef: "Chef Hassan",
      temperature: "500°F",
      isActive: true
    },
    {
      id: 4,
      name: "Sauce Station",
      description: "Our signature sauces and marinades prepared daily",
      image: "https://images.pexels.com/photos/6107787/pexels-photo-6107787.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
      activity: "Mixing tahini sauce",
      chef: "Chef Amira",
      temperature: "Cool prep",
      isActive: false
    }
  ];

  const kitchenStats = [
    { label: "Orders Today", value: "247", icon: "ShoppingBag", color: "text-primary" },
    { label: "Fresh Batches", value: "18", icon: "ChefHat", color: "text-accent" },
    { label: "Active Chefs", value: "6", icon: "Users", color: "text-trust" },
    { label: "Avg Prep Time", value: "12min", icon: "Clock", color: "text-conversion" }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCamera((prev) => (prev + 1) % kitchenCameras.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [kitchenCameras.length]);

  const selectCamera = (index) => {
    setActiveCamera(index);
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-success-100 text-success-700 px-4 py-2 rounded-full font-cta font-semibold mb-4">
            <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
            <span>LIVE FROM OUR KITCHEN</span>
          </div>
          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Transparency in Every Dish
          </h2>
          <p className="font-body text-xl text-text-secondary max-w-3xl mx-auto">
            See the passion and craftsmanship that goes into every meal. Our open kitchen policy means you can watch your food being prepared with love and expertise.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Camera Feed */}
          <div className="lg:col-span-2">
            <div className="relative bg-text-primary rounded-2xl overflow-hidden shadow-warm-lg">
              {/* Live Indicator */}
              <div className="absolute top-4 left-4 z-10 flex items-center space-x-2 bg-error text-white px-3 py-1 rounded-full text-sm font-cta font-semibold">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span>LIVE</span>
              </div>

              {/* Camera Controls */}
              <div className="absolute top-4 right-4 z-10 flex space-x-2">
                <button className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-organic backdrop-blur-sm">
                  <Icon name="Volume2" size={18} />
                </button>
                <button className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-organic backdrop-blur-sm">
                  <Icon name="Maximize" size={18} />
                </button>
              </div>

              {/* Main Feed */}
              <div className="relative h-96 lg:h-[500px]">
                <Image
                  src={kitchenCameras[activeCamera].image}
                  alt={kitchenCameras[activeCamera].name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-text-primary/80 via-transparent to-transparent"></div>
                
                {/* Feed Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-headline text-2xl font-bold mb-1">
                        {kitchenCameras[activeCamera].name}
                      </h3>
                      <p className="text-white/80">
                        {kitchenCameras[activeCamera].description}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-accent text-lg font-cta font-semibold">
                        {kitchenCameras[activeCamera].temperature}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="ChefHat" size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="font-cta font-semibold">
                          {kitchenCameras[activeCamera].chef}
                        </div>
                        <div className="text-sm text-white/70">
                          {kitchenCameras[activeCamera].activity}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="Eye" size={16} />
                      <span>127 watching</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Camera Selection */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {kitchenCameras.map((camera, index) => (
                <button
                  key={camera.id}
                  onClick={() => selectCamera(index)}
                  className={`relative p-3 rounded-lg transition-organic ${
                    index === activeCamera
                      ? 'bg-primary text-white shadow-warm'
                      : 'bg-surface hover:bg-primary-50 text-text-primary'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${
                      camera.isActive ? 'bg-success-500' : 'bg-gray-400'
                    }`}></div>
                    <span className="font-cta font-semibold text-sm">
                      {camera.name}
                    </span>
                  </div>
                  <p className="text-xs opacity-80 text-left">
                    {camera.activity}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Kitchen Stats & Info */}
          <div className="space-y-6">
            {/* Real-time Stats */}
            <div className="card-surface">
              <h3 className="font-headline text-xl font-bold text-text-primary mb-6">
                Kitchen Activity Today
              </h3>
              <div className="space-y-4">
                {kitchenStats.map((stat, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center`}>
                        <Icon name={stat.icon} size={20} className={stat.color} />
                      </div>
                      <span className="font-body text-text-secondary">
                        {stat.label}
                      </span>
                    </div>
                    <span className="font-cta font-bold text-xl text-text-primary">
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quality Commitments */}
            <div className="card-surface">
              <h3 className="font-headline text-xl font-bold text-text-primary mb-6">
                Our Quality Promise
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-success mt-1" />
                  <div>
                    <h4 className="font-cta font-semibold text-text-primary">Fresh Daily</h4>
                    <p className="text-sm text-text-secondary">All ingredients sourced and prepared fresh every morning</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-success mt-1" />
                  <div>
                    <h4 className="font-cta font-semibold text-text-primary">Open Kitchen</h4>
                    <p className="text-sm text-text-secondary">Complete transparency in our cooking process</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle" size={20} className="text-success mt-1" />
                  <div>
                    <h4 className="font-cta font-semibold text-text-primary">Expert Chefs</h4>
                    <p className="text-sm text-text-secondary">Traditional training with modern food safety standards</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visit Kitchen CTA */}
            <div className="bg-gradient-to-br from-primary to-primary-600 rounded-lg p-6 text-white">
              <h3 className="font-headline text-xl font-bold mb-3">
                Visit Our Kitchen
              </h3>
              <p className="text-white/90 mb-4 text-sm">
                Schedule a behind-the-scenes tour and meet our passionate chefs
              </p>
              <button className="w-full bg-white text-primary font-cta font-semibold py-3 rounded-lg hover:bg-primary-50 transition-organic">
                Book Kitchen Tour
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveKitchen;